import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { filter } from 'rxjs/operators';
import { BnUsuarioService } from '@bndes/bndes-ux4/bn-usuario';
import { ROLE_GESTOR_CONSULTA, ROLE_AGENTE_CONSULTA } from 'src/app/constantes/permissao.constants';
import { AgenteFinanceiro } from 'src/app/model/agente-financeiro.model';
import { Tranche } from 'src/app/model/tranche.model';
import { RenunciaLimiteAdicional, JanelaLimiteAdicionalResponse } from 'src/app/model/renuncia-limite-adicional.model';
import { PermissaoService } from 'src/app/shared/permissao.service';
import { AgenteFinanceiroService } from 'src/app/shared/services/agente-financeiro.service';
import { TrancheService } from 'src/app/shared/services/tranche.service';
import { RenunciaLimiteAdicionalService } from '../renuncia-limite-adicional.service';
import { LimiteAdicionalTrancheAgenteFinanceiro } from 'src/app/model/limite-adicional-tranche-agente-financeiro.model';
import { TarefasSistemaService } from 'src/app/features/tarefas/sistema/tarefas-sistema.service';
import moment from 'moment';

@Component({
  selector: 'app-renuncia-limite-adicional-pesquisa',
  templateUrl: './pesquisa.component.html',
  styleUrls: ['./pesquisa.component.scss'],
  standalone: false,
})
export class PesquisaComponent implements OnInit {

  public pesquisaForm: UntypedFormGroup;
  public cnpjUsuario: string;

  public agentesFinanceiros: AgenteFinanceiro[] = [];
  public tranches: Tranche[] = [];
  public janelas: JanelaLimiteAdicionalResponse[] = [];
  public limitesAdicionais: LimiteAdicionalTrancheAgenteFinanceiro[] = [];
  public renuncias: RenunciaLimiteAdicional[] = [];
  public janelaAtiva: JanelaLimiteAdicionalResponse | null = null;
  public ultimaJanelaEncerrada: JanelaLimiteAdicionalResponse | null = null;

  public podeEscolherAgente = false;
  public podeSolicitar = false;
  public pesquisado = false;

  public textoVigencia: string;
  public jaTemSolicitacao = false;
  public dataSistema: Date;

  private permissaoCarregada = false;

  constructor(
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private permissaoService: PermissaoService,
    private bnUsuarioService: BnUsuarioService,
    private agenteService: AgenteFinanceiroService,
    private trancheService: TrancheService,
    private service: RenunciaLimiteAdicionalService,
    private sistemaService: TarefasSistemaService
  ) {}

  @HostListener('window:keydown.enter', ['$event'])
  keyEvent(event: KeyboardEvent) {
    const target = event.target as HTMLElement;

    if (target.closest('ng-select')) {
      return;
    }

    this.buscar();
  }

  ngOnInit(): void {
    this.obterData();
    this.construirFormulario();

    this.permissaoService.getPermissoesUsuarioAutenticado().subscribe((resp: any) => {
      this.podeEscolherAgente = resp && resp[ROLE_GESTOR_CONSULTA];
      this.podeSolicitar = resp && resp[ROLE_AGENTE_CONSULTA];
      this.permissaoCarregada = true;
      this.preSelecionarAgenteSeAf();
    });

    this.bnUsuarioService.usuario$.pipe(filter(usuario => !!usuario)).subscribe(usuario => {
      this.cnpjUsuario = usuario.cnpj ? usuario.cnpj : 'BNDES';
      this.preSelecionarAgenteSeAf();
    });

    this.buscarAgentesFinanceiros();
    this.buscarTranches();
    this.buscarJanelas();
  }

  public obterData() {
    this.sistemaService.obterDataSistema().subscribe((dataAtual: any) => {
      this.dataSistema = moment(dataAtual.nowDateISO, 'YYYYMMDD').toDate();
    })
  }

  private construirFormulario() {
    this.pesquisaForm = this.formBuilder.group({
      agenteFinanceiro: [null],
      tranche: [''],
      limiteAdicional: [''],
      janela: [''],
    });

    this.pesquisaForm.get('tranche').valueChanges.subscribe(() => {
      this.pesquisaForm.get('limiteAdicional').setValue('');
      this.buscarLimitesAdicionais();

      if (!this.podeEscolherAgente) {
        let agente: AgenteFinanceiro = this.pesquisaForm.get('agenteFinanceiro').value;
        this.verificarSolicitacaoExistente(agente?.id);
      }
    });
  }

  private buscarAgentesFinanceiros() {
    this.agenteService.getAgentesFinanceirosNestaModalidade(true).subscribe(agentes => {
      this.agentesFinanceiros = agentes;
      this.preSelecionarAgenteSeAf();
    });
  }

  
  private preSelecionarAgenteSeAf() {
    if (!this.permissaoCarregada || !this.cnpjUsuario || !this.agentesFinanceiros.length) {
      return;
    }
    const control = this.pesquisaForm.get('agenteFinanceiro');
    if (this.podeEscolherAgente) {
      control.enable();
      return;
    }
    const af = this.agentesFinanceiros.find(a => a.cnpj === this.cnpjUsuario);
    control.setValue(af || null);
    control.disable();
    this.verificarSolicitacaoExistente(af ? af.id : null);
  }

  
  private verificarSolicitacaoExistente(ideAgente: number) {
    if (!ideAgente) {
      this.jaTemSolicitacao = false;
      return;
    }
    this.service.existeSolicitacao(ideAgente).subscribe(existe => {
      this.jaTemSolicitacao = existe;
    });
  }

  private buscarTranches() {
    this.trancheService.getTranches().subscribe(tranches => {
      this.tranches = tranches;
    });
  }

  private buscarJanelas() {
    this.service.getJanelas().subscribe(janelas => {
      this.janelas = janelas || [];

      this.definirJanelasReferencia();
      this.montarTextoVigencia();
    });
  }

  private definirJanelasReferencia() {
    const hoje = moment(this.dataSistema).format('YYYY-MM-DD');

    this.janelaAtiva = this.janelas.find(j =>
      moment(j.dataInicioVigencia).format('YYYY-MM-DD') <= hoje &&
      moment(j.dataFimVigencia).format('YYYY-MM-DD') >= hoje
    ) || null;

    this.ultimaJanelaEncerrada = this.janelas.filter(j => moment(j.dataFimVigencia).format('YYYY-MM-DD') < hoje)
                                             .sort((a, b) =>
                                                new Date(b.dataFimVigencia).getTime()
                                              - new Date(a.dataFimVigencia).getTime()
                                             )[0] || null;
  }

  public formatarJanelaLabel(janela: JanelaLimiteAdicionalResponse): string {
    return this.formatarData(janela.dataInicioVigencia) + '-' + this.formatarData(janela.dataFimVigencia);
  }

  private montarTextoVigencia() {
    if (!this.janelas.length) {
      this.textoVigencia = 'Período de Renúncia Limite Adicional indisponível';
      return;
    }

    const janela = this.janelaAtiva || this.ultimaJanelaEncerrada;

    if (!janela) {
      return;
    }

    this.textoVigencia = 'Período para renúncia de '
                        + this.formatarData(janela.dataInicioVigencia) + ' a '
                        + this.formatarData(janela.dataFimVigencia);

    if (!this.janelaAtiva) {
      this.textoVigencia += ' encerrado';
    }
  }

  private formatarData(data: string): string {
    const d = new Date(data);
    return ('0' + d.getDate()).slice(-2) + '/' + ('0' + (d.getMonth() + 1)).slice(-2) + '/' + d.getFullYear();
  }

  comparaComFuncao = (item1: AgenteFinanceiro, item2: AgenteFinanceiro): boolean => item1?.id === item2?.id;

  public buscar() {
    if (!this.pesquisaForm.get('tranche').value) {
      return;
    }

    const agente: AgenteFinanceiro = this.pesquisaForm.get('agenteFinanceiro').value;
    const ideAgente = agente && agente.id ? agente.id : undefined;
    const ideTranche = this.pesquisaForm.get('tranche').value || undefined;
    const ideJanela = this.pesquisaForm.get('janela').value || undefined;
    const ideLimiteAdicionalPorTrancheAgenteFinanceiro = this.pesquisaForm.get('limiteAdicional').value || undefined;    

    this.service.buscarRenuncias(ideAgente, ideTranche, ideJanela, ideLimiteAdicionalPorTrancheAgenteFinanceiro).subscribe(renuncias => {
      this.renuncias = renuncias || [];
      this.pesquisado = true;
    });
  }

  public limpar() {
    if (this.podeEscolherAgente) {
      this.pesquisaForm.get('agenteFinanceiro').setValue(null);
    }
    this.pesquisaForm.get('tranche').setValue('');
    this.pesquisaForm.get('janela').setValue('');
    this.pesquisado = false;
  }

  public irParaSolicitacao() {
    if (!this.janelaAtiva) {
      return;
    }

    const ideTranche = this.pesquisaForm.get('tranche').value;
    const agente: AgenteFinanceiro = this.pesquisaForm.get('agenteFinanceiro').value;

    if (!ideTranche || !agente || agente.id == null) {
      return;
    }

    this.router.navigate(['renuncia-limite-adicional', 'solicitacao', ideTranche, agente.id]);
  }

  public possuiJanelaRenuncia() : boolean {
    return this.janelas.length > 0;
  }

  private buscarLimitesAdicionais() {
    const agente: AgenteFinanceiro = this.pesquisaForm.get('agenteFinanceiro').value;

    this.service.getLimiteAdicionalPermitido(agente?.id).subscribe(limites => {
      this.limitesAdicionais = limites;
    });
  }

}